import React, { useState, useEffect, useContext, useMemo } from 'react';
import api from '../api/axios';
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { FaPlus, FaSyringe, FaEdit, FaCalendarAlt, FaFlask, FaTimes, FaCheck } from "react-icons/fa";
import { DataGridTable, SearchBar, FilterSelect, FilterDateGroup, FiltersContainer } from "../components";
import { toast, ToastContainer } from "react-toastify";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "react-toastify/dist/ReactToastify.css";
import "../styles/common.css";
import "./Sante.css";

export default function Sante() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();

  const STATUT_MAPPING = useMemo(() => ({
    'traite': t.treated,
    'non_traite': t.notTreated,
  }), [t]);
  const [interventions, setInterventions] = useState([]);
  const [animaux, setAnimaux] = useState([]);
  const [troupeaux, setTroupeaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMassEditForm, setShowMassEditForm] = useState(false);
  const [massEditFormData, setMassEditFormData] = useState({
    date: '',
    type_intervention: '',
    statut: '',
    produit: '',
    description: '',
    prochaine_intervention: '',
  });
  const [type_intervention, setTypeIntervention] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('sante-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('sante-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  const fetchSante = async () => {
      try {
      const params = new URLSearchParams();
      if (type_intervention) params.append('type_intervention', type_intervention);
      if (searchTerm) params.append('search', searchTerm);
      if (startDate) params.append('date_after', startDate);
        if (endDate) params.append('date_before', endDate);
      
      const response = await api.get("/api/sante/", { params });
        setInterventions(response.data);
        setLoading(false);
      } catch (err) {
        setError(t.errorLoadingHealth);
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchSante();
  }, [type_intervention, searchTerm, startDate, endDate]);

  useEffect(() => {
    const fetchAnimaux = async () => {
      try {
        const [animauxRes, troupeauxRes] = await Promise.all([
          api.get("/api/animaux/"),
          api.get("/api/troupeaux/"),
        ]);
        setAnimaux(animauxRes.data);
        setTroupeaux(troupeauxRes.data);
      } catch (err) {
        console.error("Erreur lors du chargement des animaux/troupeaux :", err);
      }
    };
    fetchAnimaux();
  }, []);
  
  const handleMassEditFormChange = (e) => {
    const { name, value } = e.target;
    setMassEditFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleMassEditSubmit = async (e) => {
    e.preventDefault();
    const dataToSend = {
      ids_fiches: selectedIds,
      champs_a_modifier: massEditFormData,
    };
     try {
      await api.put('/api/traitement-masse/modification/', dataToSend);
      toast.success(t.prophylaxisUpdatedSuccess);
      setShowMassEditForm(false);
      setSelectedIds([]);
      fetchSante();
    } catch (err) {
      console.error('Erreur lors de la mise à jour en masse:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          t.massUpdateError;
      toast.error(errorMessage);
    }
  };

  // Fonctions pour ajuster le zoom

  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    // Fonction pour obtenir le nom du troupeau depuis l'animal
    const getTroupeauName = (animalId) => {
      if (!animalId) return t.notProvided;
      const animal = animaux.find(a => a.id === animalId);
      if (!animal) return t.notProvided;
      
      // Si troupeau est un objet avec nom
      if (typeof animal.troupeau === 'object' && animal.troupeau?.nom) {
        return animal.troupeau.nom;
      }
      // Si troupeau est un ID (nombre), chercher dans la liste des troupeaux
      if (typeof animal.troupeau === 'number') {
        const troupeau = troupeaux.find(t => t.id === animal.troupeau);
        if (troupeau) {
          return troupeau.nom || t.notProvided;
        }
      }
      return t.notProvided;
    };

    return interventions.map(intervention => {
      const animal = animaux.find(a => a.id === intervention.animal);
      return {
        id: intervention.id,
        animal: animal ? animal.matricule : t.unknown,
        troupeau: getTroupeauName(intervention.animal),
        type_intervention: intervention.type_intervention,
        statut: intervention.statut,
        date: intervention.date,
        prochaine_intervention: intervention.prochaine_intervention || t.notProvided,
        description: intervention.description || "",
        animalId: intervention.animal,
      };
    });
  }, [interventions, animaux, troupeaux, t]);

  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'animal',
      headerName: t.animal,
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'troupeau',
      headerName: t.herd,
      width: 180,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'type_intervention',
      headerName: t.interventionType,
      width: 180,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'statut',
      headerName: t.status,
      width: 130,
      flex: 1,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const statut = params.value;
        const statutClass = statut === 'traite' ? 'status-traite' : 'status-NonTraite';
        return (
          <span className={`status-badge ${statutClass}`}>
            {STATUT_MAPPING[statut] || statut}
          </span>
        );
      },
    },
    {
      field: 'date',
      headerName: t.date,
      width: 130,
      flex: 1,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'prochaine_intervention',
      headerName: t.nextIntervention,
      width: 180,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
  ], [t, STATUT_MAPPING]);

  const detailsColumn = useMemo(() => [
    {
      field: 'details',
      headerName: t.details,
      width: 130,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <button
          onClick={() => navigate(`/sante/${params.row.id}/detail`)}
          className="grid-action-button"
          style={{ fontSize: `${1.3 * zoomLevel}rem` }}
        >
          {t.viewMore}
        </button>
      ),
    },
  ], [navigate, zoomLevel, t]);

  const adminColumns = useMemo(() => {
    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_SANTE);
    if (!canEdit) return [];
    
    return [
      {
        field: 'actions',
        headerName: t.actions,
        width: 130,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <button
            onClick={() => navigate(`/sante/${params.row.id}/modifier`)}
            className="grid-action-button"
            style={{ fontSize: `${1.3 * zoomLevel}rem` }}
          >
            {t.edit}
          </button>
        ),
      },
    ];
  }, [user, navigate, zoomLevel, t]);

  const columns = useMemo(() => {
    const isAdmin = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_SANTE);
    if (isAdmin && adminColumns.length > 0) {
      return [
        {
          field: 'checkbox',
          headerName: '',
          width: 60,
          sortable: false,
          renderCell: (params) => (
            <input
              type="checkbox"
              checked={selectedIds.includes(params.row.id)}
              onChange={() => {
                setSelectedIds(prev =>
                  prev.includes(params.row.id)
                    ? prev.filter(id => id !== params.row.id)
                    : [...prev, params.row.id]
                );
              }}
            />
          ),
        },
        ...baseColumns,
        ...detailsColumn,
        ...adminColumns,
      ];
    }
    return [...baseColumns, ...detailsColumn];
  }, [user, baseColumns, detailsColumn, adminColumns, selectedIds]);

  if (loading) {
    return (
      <div className="sante-page">
        <div className="loading-container">
          <p className="loading-text">{t.loadingData}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sante-page">
        <div className="title-badge">
          <FaSyringe className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sante-page">
      <div className="title-badge">
        <FaSyringe className="page-title-icon" aria-hidden="true" />
        <span>{t.pages["/sante"] || "PROPHYLAXIE ET TRAITEMENT CURATIF"}</span>
      </div>

      {/* Boutons d'action admin */}
        {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_SANTE)) && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-sante")}
          >
            <FaPlus />
            <span>{t.addHealthRecord}</span>
          </button>
          <button
            type="button"
            className="save-but"
        onClick={() => navigate("/traitement-masse")}
      >
            <FaFlask />
            <span>{t.massTreatment}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => setShowMassEditForm(true)}
            disabled={selectedIds.length === 0}
          >
            <FaEdit />
            <span>{t.editSelection} ({selectedIds.length})</span>
          </button>
        </div>
    )}

      {/* Formulaire de modification en masse */}
         {showMassEditForm && (
        <div className="mass-edit-form-container">
          <div className="mass-edit-form-header">
            <h3>{t.editSelectedRecords}</h3>
            <button
              type="button"
              onClick={() => setShowMassEditForm(false)}
              className="close-form-button"
            >
              <FaTimes />
            </button>
          </div>
          <form onSubmit={handleMassEditSubmit} className="mass-edit-form">
            <div className="form-section-container">
              <div className="form">
                <label htmlFor="mass-edit-date">{t.date}</label>
                <input
                  type="date"
                  id="mass-edit-date"
                  name="date"
                  value={massEditFormData.date}
                  onChange={handleMassEditFormChange}
                />
              </div>
              <div className="form">
                <label htmlFor="mass-edit-type-intervention">{t.interventionType}</label>
                <input
                  type="text"
                  id="mass-edit-type-intervention"
                  name="type_intervention"
                  value={massEditFormData.type_intervention}
                  onChange={handleMassEditFormChange}
                  placeholder={t.interventionTypePlaceholder}
                />
              </div>
              <div className="form">
                <label htmlFor="mass-edit-statut">{t.status}</label>
        <select
                  id="mass-edit-statut"
          name="statut"
          value={massEditFormData.statut}
          onChange={handleMassEditFormChange}
        >
                  <option value="">{t.selectStatus}</option>
          <option value="traite">{t.treated}</option>
          <option value="non_traite">{t.notTreated}</option>
        </select>
              </div>
              <div className="form">
                <label htmlFor="mass-edit-produit">{t.product}</label>
                <input
                  type="text"
                  id="mass-edit-produit"
                  name="produit"
                  value={massEditFormData.produit}
                  onChange={handleMassEditFormChange}
                  placeholder={t.productName}
                />
              </div>
              <div className="form">
                <label htmlFor="mass-edit-description">{t.description}</label>
                <input
                  type="text"
                  id="mass-edit-description"
                  name="description"
                  value={massEditFormData.description}
                  onChange={handleMassEditFormChange}
                  placeholder={t.description}
                />
              </div>
              <div className="form">
                <label htmlFor="mass-edit-prochaine-intervention">{t.nextIntervention}</label>
                <input
                  type="date"
                  id="mass-edit-prochaine-intervention"
                  name="prochaine_intervention"
                  value={massEditFormData.prochaine_intervention}
                  onChange={handleMassEditFormChange}
                />
              </div>
            </div>
            <div className="actions">
              <button
                type="button"
                className="cancel-but"
                onClick={() => setShowMassEditForm(false)}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="submit"
                className="save-but"
              >
                <FaCheck />
                <span>{t.save}</span>
              </button>
            </div>
            </form>
          </div>
        )} 

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder={t.searchByIntervention}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
        />
      </div>

      {/* Filtres */}
      <FiltersContainer title={t.filters}>
        <FilterSelect
          name="type_intervention"
          value={type_intervention}
          onChange={(e) => setTypeIntervention(e.target.value)}
          options={[
            { value: "vaccination", label: t.vaccination },
            { value: "traitement", label: t.treatment },
            { value: "vermifuge", label: t.deworming },
            { value: "autre", label: t.other }
          ]}
          placeholder={t.filterByInterventionType}
          className="filtre-type"
          width="220px"
        />
        <FilterDateGroup
          label={t.startDate}
          name="date_debut"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <FilterDateGroup
          label={t.endDate}
          name="date_fin"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </FiltersContainer>

      {/* DataGrid */}
      {interventions.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">
            {type_intervention || startDate || endDate || searchTerm
              ? t.noInterventionsFound
              : t.noInterventionsRegistered}
          </p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="sante-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'date', sort: 'desc' }}
          pageSize={25}
          containerClassName="sante-grid-container"
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
